import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Search, ChevronDown, ChevronUp, MessageSquare, Send, Loader2, UserX, ChevronRight } from "lucide-react";

function ContactForm() {
  const [form, setForm] = useState({ subject: "", message: "", category: "general" });
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setMsg("");
    try {
      await api.post("/help/contact", form);
      setMsg("Thanks! Our team will get back to you soon.");
      setForm({ subject: "", message: "", category: "general" });
    } catch (e) { setMsg("Failed. Please try again."); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-2.5 p-4 rounded-2xl bg-white/5 border border-white/10" data-testid="contact-form">
      <h3 className="font-outfit font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#00E5FF]"/>Contact Support</h3>
      <select value={form.category} onChange={e=>setForm(f=>({...f, category:e.target.value}))} className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none text-sm">
        {["general","account","wallet","payment","technical","report"].map(c => <option key={c} value={c} className="bg-[#0B0F19]">{c}</option>)}
      </select>
      <input required placeholder="Subject" value={form.subject} onChange={e=>setForm(f=>({...f, subject:e.target.value}))} className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none text-sm" data-testid="contact-subject"/>
      <textarea required rows={4} placeholder="Describe your issue..." value={form.message} onChange={e=>setForm(f=>({...f, message:e.target.value}))} className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 outline-none text-sm" data-testid="contact-message"/>
      {msg && <div className="text-xs text-emerald-400">{msg}</div>}
      <button data-testid="contact-submit" disabled={loading} className="w-full py-2.5 rounded-full bg-[#00E5FF] text-black text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-50">
        {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Send className="w-3.5 h-3.5"/>Send</>}
      </button>
    </form>
  );
}

export default function Help() {
  const [data, setData] = useState({ categories: [], items: [] });
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => { api.get("/help/faq").then(r => setData(r.data)); }, []);

  const filtered = data.items.filter(f =>
    (filter === "All" || f.category === filter) &&
    (search === "" || f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="px-4 py-6">
      <h1 className="font-outfit font-extrabold text-3xl tracking-tighter mb-1">Help & Support</h1>
      <p className="text-white/60 text-sm mb-4">Frequently asked questions</p>

      <Link to="/delete-account" data-testid="help-delete-account" className="mb-4 flex items-center gap-3 p-3 rounded-2xl bg-[#FF2A5F]/10 border border-[#FF2A5F]/30 active:scale-[0.99]">
        <div className="w-10 h-10 rounded-xl bg-[#FF2A5F]/15 flex items-center justify-center"><UserX className="w-4 h-4 text-[#FF2A5F]"/></div>
        <div className="flex-1">
          <div className="font-semibold text-sm">Delete my Account/Data</div>
          <div className="text-[10px] text-white/60">Recoverable for 30 days</div>
        </div>
        <ChevronRight className="w-4 h-4 text-white/30"/>
      </Link>

      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search FAQ..."
          className="w-full pl-9 pr-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF] text-sm" data-testid="faq-search"/>
      </div>

      <div className="flex gap-1.5 overflow-x-auto scrollbar-hide mb-4 -mx-4 px-4">
        {["All", ...data.categories].map(c => (
          <button key={c} onClick={()=>setFilter(c)} data-testid={`faq-cat-${c}`}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${filter===c?"border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]":"border-white/10 bg-white/5 text-white/70"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="space-y-2 mb-6" data-testid="faq-list">
        {filtered.map(f => (
          <button key={f.id} onClick={()=>setOpenId(o=>o===f.id?null:f.id)} data-testid={`faq-${f.id}`}
            className="w-full text-left p-3 rounded-2xl bg-white/5 border border-white/10">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm flex-1">{f.q}</span>
              {openId === f.id ? <ChevronUp className="w-4 h-4 text-white/50 flex-shrink-0"/> : <ChevronDown className="w-4 h-4 text-white/50 flex-shrink-0"/>}
            </div>
            <div className="text-[10px] text-[#00E5FF] mt-0.5">{f.category}</div>
            {openId === f.id && <p className="text-xs text-white/80 mt-2 leading-relaxed">{f.a}</p>}
          </button>
        ))}
        {filtered.length === 0 && <p className="text-center text-white/40 text-sm py-6">No FAQ matches your search.</p>}
      </div>

      <ContactForm/>
    </div>
  );
}
